import { useContext, useEffect } from 'react';
import { CONVERSATION_CONSTANTS } from '../config/miaw/constants';
import { CAContext } from '../contexts/CAContext';
import { MiawChatContext } from '../contexts/MiawChatContext';
import { setChatSessionType } from '../utils/session-storage-utils';
import { useMIAWChatEnd } from './miaw/useMIAWChatEnd';
import { useMIAWChatMessage } from './miaw/useMIAWChatMessage';
import { useMIAWChatRetrieve } from './miaw/useMIAWChatRetrieve';
import { useMIAWStartChat } from './miaw/useMIAWChatStart';
import { useCustomChatEvents } from './custom-hooks/useCustomChatEvents';

export const useMIAWHooks = () => {
  const {
    setChatState,
    chatState: { chatWindow, chatSession },
  } = useContext(CAContext);
  const { setState: setMIAWContext } = useContext(MiawChatContext);

  const { sendChatEvents } = useCustomChatEvents();

  // Initialize all MIAW hooks
  const { loading: miawChatStartLoading, trigger: triggerMiawStartChat, data: miawChatStartData, error: miawChatStartError } = useMIAWStartChat();

  const {
    data: miawChatRetrieveData,
    trigger: miawChatRetrieveTrigger,
    loading: miawChatRetrieveLoading,
    error: miawChatRetrieveError,
  } = useMIAWChatRetrieve();

  const { data: miawChatEndData, trigger: triggerMIAWChatEnd, loading: miawChatEndLoading, error: miawChatEndError } = useMIAWChatEnd();

  const { data: miawChatMessageData, trigger: triggerMIAWChatMessage, loading: miawChatMessageLoading, error: miawChatMessageError } = useMIAWChatMessage();

  // Set up MIAW hooks in context
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      startMiawChatSession: triggerMiawStartChat,
    }));
  }, [triggerMiawStartChat, setMIAWContext]);

  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      sendMiawChatMessage: triggerMIAWChatMessage,
    }));
  }, [triggerMIAWChatMessage, setMIAWContext]);

  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      endMiawChatSession: triggerMIAWChatEnd,
    }));
  }, [triggerMIAWChatEnd, setMIAWContext]);

  // Handle MIAW chat start response
  useEffect(() => {
    if (miawChatStartData) {
      setChatSessionType('miawChatSession');
      setChatState((prevState) => ({ ...prevState, chatSession: 'miawChatSession' }));
      sendChatEvents('onChatStart', {});
    }
  }, [miawChatStartData, setChatState]);

  // Handle MIAW chat retrieve response
  useEffect(() => {
    if (miawChatRetrieveData) {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: miawChatRetrieveData.messages,
        conversationId: miawChatRetrieveData.conversationId,
        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
        agentName: miawChatRetrieveData.agentName,
      }));
      sendChatEvents('onSessionContinuation', {});
    }
  }, [miawChatRetrieveData, setMIAWContext]);

  useEffect(() => {
    if (miawChatEndData) {
      setChatState((prevState) => ({
        ...prevState,
        chatSession: 'endChatSession',
        isPrivacyPolicyAccepted: false,
        unreadMessages: 0,
      }));
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: [],
        conversationId: '',
        agentName: '',
        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION,
        endChatData: miawChatEndData, // Set end chat data in MIAW context
      }));
      sendChatEvents('onChatEnd', {});
    }
  }, [miawChatEndData]);

  useEffect(() => {
    console.log({ miawChatMessageError }, { miawChatRetrieveError });
    const hasError = miawChatMessageError || miawChatRetrieveError;
    if (hasError) {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: true,
      }));
    } else {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: false,
      }));
    }
  }, [miawChatMessageError, miawChatRetrieveError]);

  useEffect(() => {
    if (miawChatStartError) {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWStartChatError: true,
      }));
    } else {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWStartChatError: false,
      }));
    }
  }, [miawChatStartError]);

  useEffect(() => {
    if (chatSession === 'initial') {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: false,
      }));
    }
  }, [chatSession]);

  /** Once chat session is initial stage , remove end chat info */
  useEffect(() => {
    if (chatSession === 'initial') {
      setMIAWContext((prevState) => ({
        ...prevState,
        endChatData: undefined,
        endChatLoading: false,
      }));
    }
  }, [chatSession]);

  /** Set endChat loading info in context */
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      endChatLoading: miawChatEndLoading,
    }));
  }, [miawChatEndLoading]);

  useEffect(() => {
    if (chatWindow === 'minimized') {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true })),
      }));
    }
  }, [chatWindow]);

  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      isMIAWApiLoading: miawChatStartLoading,
    }));
  }, [miawChatStartLoading]);

  useEffect(() => {
    if (miawChatEndError) {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWEndChatError: true,
      }));
    }
  }, [miawChatEndError]);

  useEffect(() => {
    if (chatSession === 'initial') {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWEndChatError: false,
      }));
    }
  }, [chatSession]);

  return {
    miawHooksData: {
      miawChatStartData,
      miawChatRetrieveData,
      miawChatEndData,
      miawChatMessageData,
    },
    miawHooksLoading: {
      miawChatStartLoading,
      miawChatRetrieveLoading,
      miawChatEndLoading,
      miawChatMessageLoading,
    },
    miawHooksError: {
      miawChatStartError,
      miawChatRetrieveError,
      miawChatEndError,
      miawChatMessageError,
    },
    miawHooksTriggers: {
      triggerMiawStartChat,
      miawChatRetrieveTrigger,
      triggerMIAWChatEnd,
      triggerMIAWChatMessage,
    },
  };
};
